import React, { useState, useMemo } from 'react';
import {
  Paper, Box, Typography, Chip, Button, RadioGroup, FormControlLabel, Radio, Grid, TextField, Switch, Alert,
} from '@mui/material';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import { Tooltip } from '@mui/material';
import { EntityDrawer, UsageMeter, FieldRow } from '../../ui';
import { formatPrice, grantExpiryTone } from './planUtils';

const EMPTY_GRANT = { planId: '', noExpiry: true, validUntil: '', reference: '', confirmName: '' };

const tomorrow = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
};

// GA company detail: which plan the company is on, how much of it they use,
// and the operator controls. `data` = GET /ga/companies/:id/subscription.
// Two separate doors on purpose: "Change plan" only ever offers public
// plans; a private plan is reachable solely through the grant drawer, which
// demands a reference and the typed company name.
const CompanySubscriptionCard = ({
  data, plans, features, companyName, onChange, changing, onGrant, onRenew, onRevoke, t,
}) => {
  const [drawer, setDrawer] = useState(null); // 'change' | 'grant' | 'renew' | 'revoke'
  const [selected, setSelected] = useState('');
  const [grant, setGrant] = useState(EMPTY_GRANT);
  const [reason, setReason] = useState('');

  const current = data?.current;
  const plan = current?.plan;
  const isGrant = current?.source === 'operator_grant';
  const isStripe = Boolean(current?.stripe);
  const unsubscribed = data?.subscribed === false;
  const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : '');
  const entitlements = data?.entitlements?.features || {};
  const meters = (features || []).filter((f) => f.kind !== 'boolean' && entitlements[f.key]?.enabled);
  const publicPlans = (plans || []).filter((pl) => pl.status !== 'archived' && pl.visibility !== 'private');
  const privatePlans = (plans || []).filter((pl) => pl.status !== 'archived' && pl.visibility === 'private');

  const validUntil = current?.validUntil ? new Date(current.validUntil) : null;
  const expiryTone = grantExpiryTone(current?.validUntil);

  const grantErrors = useMemo(() => {
    const errs = {};
    if (drawer === 'grant' && !grant.planId) errs.planId = t('ga.validation.required');
    if (grant.reference.trim().length < 3) errs.reference = t('At least 3 characters (agreement, invoice or e-transfer id)', { defaultValue: 'At least 3 characters (agreement, invoice or e-transfer id)' });
    if (!grant.noExpiry) {
      if (!grant.validUntil) errs.validUntil = t('ga.validation.required');
      else if (grant.validUntil < tomorrow()) errs.validUntil = t('Pick a date in the future', { defaultValue: 'Pick a date in the future' });
    }
    if (drawer === 'grant' && grant.confirmName.trim().toLowerCase() !== (companyName || '').trim().toLowerCase()) {
      errs.confirmName = t("Type the company's exact name to confirm", { defaultValue: "Type the company's exact name to confirm" });
    }
    return errs;
  }, [drawer, grant, companyName, t]);
  const grantValid = Object.keys(grantErrors).length === 0;

  const close = () => { if (!changing) setDrawer(null); };
  const openChange = () => { setSelected(plan?._id || ''); setDrawer('change'); };
  const openGrant = () => { setGrant({ ...EMPTY_GRANT, planId: privatePlans.length === 1 ? privatePlans[0]._id : '' }); setDrawer('grant'); };
  const openRenew = () => { setGrant({ ...EMPTY_GRANT, planId: plan?._id || '', noExpiry: !current?.validUntil, validUntil: current?.validUntil ? current.validUntil.slice(0, 10) : '' }); setDrawer('renew'); };
  const openRevoke = () => { setReason(''); setDrawer('revoke'); };

  const grantBody = () => ({
    validUntil: grant.noExpiry ? null : grant.validUntil,
    reference: grant.reference.trim(),
  });

  const confirmChange = async () => {
    if (!selected || selected === plan?._id) { setDrawer(null); return; }
    if (await onChange(selected)) setDrawer(null);
  };
  const confirmGrant = async () => {
    if (!grantValid) return;
    if (await onGrant({ planId: grant.planId, confirmCompanyName: grant.confirmName.trim(), ...grantBody() })) setDrawer(null);
  };
  const confirmRenew = async () => {
    if (!grantValid) return;
    if (await onRenew(grantBody())) setDrawer(null);
  };
  const confirmRevoke = async () => {
    if (!reason.trim()) return;
    if (await onRevoke(reason.trim())) setDrawer(null);
  };

  const expiryFields = (
    <>
      <FormControlLabel
        control={<Switch checked={grant.noExpiry} onChange={(e) => setGrant((g) => ({ ...g, noExpiry: e.target.checked }))} />}
        label={t('No expiry — the grant stays until it is revoked', { defaultValue: 'No expiry — the grant stays until it is revoked' })}
        sx={{ mb: 1 }}
      />
      {!grant.noExpiry && (
        <FieldRow label={t('Valid until', { defaultValue: 'Valid until' })} required error={grantErrors.validUntil}>
          <TextField
            fullWidth
            type="date"
            value={grant.validUntil}
            onChange={(e) => setGrant((g) => ({ ...g, validUntil: e.target.value }))}
            inputProps={{ min: tomorrow(), 'aria-label': t('Valid until', { defaultValue: 'Valid until' }) }}
            helperText={t('Ends at 23:59 UTC on that day. The company drops to the default plan afterwards.', { defaultValue: 'Ends at 23:59 UTC on that day. The company drops to the default plan afterwards.' })}
          />
        </FieldRow>
      )}
      <FieldRow label={t('Reference', { defaultValue: 'Reference' })} required error={grantErrors.reference}>
        <TextField
          fullWidth
          value={grant.reference}
          onChange={(e) => setGrant((g) => ({ ...g, reference: e.target.value }))}
          inputProps={{ maxLength: 200, 'aria-label': t('Reference', { defaultValue: 'Reference' }) }}
          placeholder={t('Agreement, invoice or e-transfer id', { defaultValue: 'Agreement, invoice or e-transfer id' })}
        />
      </FieldRow>
    </>
  );

  return (
    <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }} data-testid="company-subscription-card">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Typography variant="subtitle1">{t('Subscription', { defaultValue: 'Subscription' })}</Typography>
        {plan ? (
          isGrant ? (
            <Chip color="secondary" icon={<HandshakeOutlinedIcon />} label={`${plan.name} · ${t('operator grant', { defaultValue: 'operator grant' })}`} data-testid="grant-chip" />
          ) : (
            <Chip color="primary" label={`${plan.name} · ${formatPrice(plan.price?.amount, plan.price?.currency)} / ${t('mo', { defaultValue: 'mo' })}`} />
          )
        ) : (
          <Chip color="warning" variant="outlined" label={unsubscribed ? t('No subscription', { defaultValue: 'No subscription' }) : t('No plan', { defaultValue: 'No plan' })} data-testid="no-subscription-chip" />
        )}
        {isStripe && <Chip size="small" variant="outlined" icon={<CreditCardOutlinedIcon />} label={t('Stripe', { defaultValue: 'Stripe' })} data-testid="stripe-chip" />}
        {current?.status === 'past_due' && (
          <Chip size="small" color="error" label={t('Payment failed · grace until {{date}}', { date: fmtDate(current.graceUntil), defaultValue: `Payment failed · grace until ${fmtDate(current.graceUntil)}` })} data-testid="past-due-chip" />
        )}
        {current?.cancelAtPeriodEnd && (
          <Chip size="small" color="warning" variant="outlined" label={t('Cancels {{date}}', { date: fmtDate(current.currentPeriodEnd), defaultValue: `Cancels ${fmtDate(current.currentPeriodEnd)}` })} />
        )}
        {current?.pendingPlanSlug && (
          <Chip size="small" color="info" variant="outlined" label={t('→ {{plan}} on {{date}}', { plan: current.pendingPlanSlug, date: fmtDate(current.currentPeriodEnd), defaultValue: `→ ${current.pendingPlanSlug} on ${fmtDate(current.currentPeriodEnd)}` })} />
        )}
        {plan && !isGrant && current?.currentPeriodEnd && !current?.cancelAtPeriodEnd && (
          <Typography variant="caption" color="text.secondary" data-testid="period-caption">
            {t('Renews {{date}}', { date: fmtDate(current.currentPeriodEnd), defaultValue: `Renews ${fmtDate(current.currentPeriodEnd)}` })}
          </Typography>
        )}
        {isGrant && (
          <Chip
            size="small"
            variant="outlined"
            color={expiryTone === 'critical' ? 'error' : expiryTone === 'warning' ? 'warning' : 'default'}
            label={validUntil
              ? t('Expires {{date}}', { date: validUntil.toLocaleDateString(), defaultValue: `Expires ${validUntil.toLocaleDateString()}` })
              : t('No expiry', { defaultValue: 'No expiry' })}
            data-testid="grant-expiry"
          />
        )}
        {current?.source && (
          <Typography variant="caption" color="text.secondary">
            {isGrant && current.grantReference
              ? `${t('ref', { defaultValue: 'ref' })} ${current.grantReference}`
              : t('via {{source}}', { source: current.source, defaultValue: `via ${current.source}` })}
            {current.startedAt ? ` · ${t('since', { defaultValue: 'since' })} ${new Date(current.startedAt).toLocaleDateString()}` : ''}
          </Typography>
        )}
        <Box sx={{ display: 'flex', gap: 1, ml: 'auto', flexWrap: 'wrap' }}>
          {isGrant ? (
            <>
              <Button size="small" variant="outlined" startIcon={<AutorenewIcon fontSize="small" />} onClick={openRenew}>
                {t('Renew', { defaultValue: 'Renew' })}
              </Button>
              <Button size="small" variant="outlined" color="error" startIcon={<BlockOutlinedIcon fontSize="small" />} onClick={openRevoke}>
                {t('Revoke grant', { defaultValue: 'Revoke grant' })}
              </Button>
            </>
          ) : (
            <>
              <Tooltip title={isStripe ? t('Paid through Stripe — the company changes plans from its own Subscription page.', { defaultValue: 'Paid through Stripe — the company changes plans from its own Subscription page.' }) : ''}>
                <span>
                  <Button size="small" variant="outlined" startIcon={<SwapHorizIcon fontSize="small" />} onClick={openChange} disabled={isStripe}>
                    {t('Change plan', { defaultValue: 'Change plan' })}
                  </Button>
                </span>
              </Tooltip>
              {privatePlans.length > 0 && (
                <Button size="small" variant="contained" color="secondary" startIcon={<HandshakeOutlinedIcon fontSize="small" />} onClick={openGrant}>
                  {t('Grant partner plan', { defaultValue: 'Grant partner plan' })}
                </Button>
              )}
            </>
          )}
        </Box>
      </Box>
      {meters.length > 0 && (
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {meters.map((f) => (
            <Grid item xs={12} sm={6} md={4} key={f.key}>
              <UsageMeter compact label={f.name} usage={entitlements[f.key]} unlimitedLabel={t('unlimited', { defaultValue: 'unlimited' })} />
            </Grid>
          ))}
        </Grid>
      )}

      <EntityDrawer
        open={drawer === 'change'}
        onClose={close}
        title={t('Change plan', { defaultValue: 'Change plan' })}
        subtitle={t('Applies immediately and is recorded in the audit log.', { defaultValue: 'Applies immediately and is recorded in the audit log.' })}
        closeLabel={t('Close', { defaultValue: 'Close' })}
        cancelLabel={t('ga.common.cancel')}
        primaryLabel={changing ? t('Saving...', { defaultValue: 'Saving...' }) : t('ga.common.save')}
        onPrimary={confirmChange}
        primaryDisabled={changing || !selected || selected === plan?._id}
        loading={changing}
      >
        <RadioGroup value={selected} onChange={(e) => setSelected(e.target.value)}>
          {publicPlans.map((pl) => (
            <FormControlLabel
              key={pl._id}
              value={pl._id}
              control={<Radio />}
              label={(
                <Box>
                  <Typography variant="body2">{pl.name} · {formatPrice(pl.price?.amount, pl.price?.currency)} / {t('mo', { defaultValue: 'mo' })}</Typography>
                  <Typography variant="caption" color="text.secondary">{pl.description}</Typography>
                </Box>
              )}
              sx={{ alignItems: 'flex-start', mb: 1 }}
            />
          ))}
        </RadioGroup>
        {privatePlans.length > 0 && (
          <Typography variant="caption" color="text.secondary">
            {t('Operator-only plans are assigned with “Grant partner plan”, not here.', { defaultValue: 'Operator-only plans are assigned with “Grant partner plan”, not here.' })}
          </Typography>
        )}
      </EntityDrawer>

      <EntityDrawer
        open={drawer === 'grant'}
        onClose={close}
        title={t('Grant partner plan', { defaultValue: 'Grant partner plan' })}
        subtitle={t('For companies billed outside the app. Only you can do this; every grant is audited.', { defaultValue: 'For companies billed outside the app. Only you can do this; every grant is audited.' })}
        closeLabel={t('Close', { defaultValue: 'Close' })}
        cancelLabel={t('ga.common.cancel')}
        primaryLabel={changing ? t('Saving...', { defaultValue: 'Saving...' }) : t('Grant', { defaultValue: 'Grant' })}
        onPrimary={confirmGrant}
        primaryDisabled={changing || !grantValid}
        loading={changing}
      >
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('This company will get every feature of the plan without paying through the app, until the grant expires or is revoked.', { defaultValue: 'This company will get every feature of the plan without paying through the app, until the grant expires or is revoked.' })}
        </Alert>
        <FieldRow label={t('Plan', { defaultValue: 'Plan' })} required error={grantErrors.planId}>
          <RadioGroup value={grant.planId} onChange={(e) => setGrant((g) => ({ ...g, planId: e.target.value }))}>
            {privatePlans.map((pl) => (
              <FormControlLabel
                key={pl._id}
                value={pl._id}
                control={<Radio />}
                label={(
                  <Box>
                    <Typography variant="body2">{pl.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{pl.description}</Typography>
                  </Box>
                )}
                sx={{ alignItems: 'flex-start', mb: 1 }}
              />
            ))}
          </RadioGroup>
        </FieldRow>
        {expiryFields}
        <FieldRow label={t('Type the company name to confirm', { defaultValue: 'Type the company name to confirm' })} required error={grantErrors.confirmName}>
          <TextField
            fullWidth
            value={grant.confirmName}
            onChange={(e) => setGrant((g) => ({ ...g, confirmName: e.target.value }))}
            placeholder={companyName || ''}
            inputProps={{ 'aria-label': t('Type the company name to confirm', { defaultValue: 'Type the company name to confirm' }) }}
          />
        </FieldRow>
      </EntityDrawer>

      <EntityDrawer
        open={drawer === 'renew'}
        onClose={close}
        title={t('Renew grant', { defaultValue: 'Renew grant' })}
        subtitle={t('Starts a new grant period on the same plan; the previous one is kept in the history.', { defaultValue: 'Starts a new grant period on the same plan; the previous one is kept in the history.' })}
        closeLabel={t('Close', { defaultValue: 'Close' })}
        cancelLabel={t('ga.common.cancel')}
        primaryLabel={changing ? t('Saving...', { defaultValue: 'Saving...' }) : t('Renew', { defaultValue: 'Renew' })}
        onPrimary={confirmRenew}
        primaryDisabled={changing || !grantValid}
        loading={changing}
      >
        {expiryFields}
      </EntityDrawer>

      <EntityDrawer
        open={drawer === 'revoke'}
        onClose={close}
        title={t('Revoke grant', { defaultValue: 'Revoke grant' })}
        subtitle={t('The company drops to the default plan immediately. Nothing is deleted.', { defaultValue: 'The company drops to the default plan immediately. Nothing is deleted.' })}
        closeLabel={t('Close', { defaultValue: 'Close' })}
        cancelLabel={t('ga.common.cancel')}
        primaryLabel={changing ? t('Saving...', { defaultValue: 'Saving...' }) : t('Revoke grant', { defaultValue: 'Revoke grant' })}
        onPrimary={confirmRevoke}
        primaryDisabled={changing || !reason.trim()}
        loading={changing}
      >
        <FieldRow label={t('Reason', { defaultValue: 'Reason' })} required>
          <TextField
            fullWidth
            multiline
            minRows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            inputProps={{ maxLength: 500, 'aria-label': t('Reason', { defaultValue: 'Reason' }) }}
          />
        </FieldRow>
      </EntityDrawer>
    </Paper>
  );
};

export default CompanySubscriptionCard;
